import { pickUnitIndex } from "../map/filters";
import { CATEGORY_LABEL, STATUS_LABEL } from "../map/model";
import type { ApiLocation, ApiUnit, Category, CategoryStyle, Status } from "../map/model";

const sidebar = () => document.getElementById("modal")!;
const content = () => document.getElementById("sidebar-content")!;
const closeBtn = () => document.getElementById("sidebar-close")!;

const STATUS_BG: Record<Status, string> = {
  active: "#dcfce7",
  in_progress: "#fef9c3",
  vacant: "#f1f5f9",
};
const STATUS_FG: Record<Status, string> = {
  active: "#166534",
  in_progress: "#854d0e",
  vacant: "#475569",
};

export function initSidebar() {
  closeBtn().addEventListener("click", closeSidebar);
  document.getElementById("modal-backdrop")?.addEventListener("click", closeSidebar);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeSidebar();
  });
}

// Açık modalın lokasyonu + o an seçili mekân (sekme geçişinde tekrar kullanılır)
let sbLoc: ApiLocation | undefined;
let sbStyles: Map<Category, CategoryStyle> = new Map();
let sbGroupIdx = 0;

/**
 * Lokasyonun mekânları, floorOrder'a göre sıralı. Her mekân bir SEKME olur.
 *
 * Bir mekân birden fazla kategoride listelenebiliyor (müşteri isteği: Hard Rock
 * Cafe hem "Restaurants & Bars" hem "Shops" altında görünsün). Bu, veride AYNI
 * kiracı + AYNI kat için birden fazla ünite satırı demek. O satırlar burada TEK
 * sekmede birleştirilir — aksi halde sidebar'da aynı isimde iki sekme çıkardı.
 *
 * Grup içindeki sıra kategori adına göre sabitlenir; böylece kartın vurgu rengi
 * `filters.ts`'teki `rank()` eşitlik bozucusuyla (aynı kat + aynı kiracı sayısı
 * → kategori adı) AYNI üniteyi seçer ve parsel rengiyle ayrışmaz.
 */
export function venueGroups(loc: ApiLocation): ApiUnit[][] {
  const ordered = [...loc.units].sort((a, b) => a.floorOrder - b.floorOrder);
  const groups: ApiUnit[][] = [];
  const byVenue = new Map<string, ApiUnit[]>();
  for (const u of ordered) {
    // Kiracı adı yoksa (boş birim) birleştirme yapılmaz — her biri kendi sekmesi.
    const key = u.tenantName ? `${u.tenantName}\u0000${u.floorLevel}` : null;
    const existing = key === null ? undefined : byVenue.get(key);
    if (existing) {
      existing.push(u);
      existing.sort((a, b) => a.category.localeCompare(b.category));
      continue;
    }
    const group = [u];
    if (key !== null) byVenue.set(key, group);
    groups.push(group);
  }
  return groups;
}

function colorFor(cat: Category): { fill: string; ink: string } {
  const s = sbStyles.get(cat);
  return { fill: s?.fill ?? "#e5e7eb", ink: s?.ink ?? "#475569" };
}

/**
 * Bir lokasyonu göster. Lokasyondaki HER MEKÂN (kiracı) bir SEKME olur; sekmeye
 * tıklayınca o mekânın kartı gösterilir (alt alta yığma/scroll YOK). `initialLevel`
 * verilirse (arama sonucu) o kata denk gelen ilk mekân seçili açılır.
 */
export function openLocationSidebar(
  loc: ApiLocation,
  styles: Map<Category, CategoryStyle>,
  initialLevel?: string,
  initialCategory?: Category,
) {
  sbLoc = loc;
  sbStyles = styles;
  // Seçim kuralı ÜNİTE düzeyinde kalır (`pickUnitIndex` — filtre/rozet tıklaması
  // kat + kategori taşıyor), sonuç ünite hangi gruptaysa o sekme açılır.
  const groups = venueGroups(loc);
  const flat = groups.flat();
  const target = flat[pickUnitIndex(flat, initialLevel, initialCategory)];
  const gi = groups.findIndex((g) => g.includes(target));
  sbGroupIdx = gi >= 0 ? gi : 0;
  render();
  show();
}

function render() {
  const loc = sbLoc;
  if (!loc) return;
  const groups = venueGroups(loc);
  if (sbGroupIdx >= groups.length) sbGroupIdx = 0;
  const group = groups[sbGroupIdx] ?? [];
  const unit = group[0];
  const accent = unit ? colorFor(unit.category).fill : "#94a3b8";
  document.getElementById("modal-card")?.style.setProperty("--accent", accent);

  // Sekmeler = mekânlar; her kiracı ayrı sekme (birden fazla mekân varsa).
  const tabs = groups.length > 1
    ? `<div class="m-floors" role="tablist">${groups
        .map((g, i) =>
          `<button type="button" role="tab" class="m-floor-btn${i === sbGroupIdx ? " active" : ""}" data-idx="${i}">${esc(g[0].tenantName ?? "For Lease")}</button>`)
        .join("")}</div>`
    : "";

  const venueName = unit?.tenantName ?? "For Lease";
  const eyebrow = `${esc(loc.name)}`
    + (loc.mapRefCode ? ` &middot; Ref ${esc(loc.mapRefCode)}` : "")
    + (unit ? ` &middot; ${esc(unit.floorLevel)}` : "");

  content().innerHTML = `
    <div class="m-body">
      <div class="m-id">${eyebrow}</div>
      <h2 class="m-title">${esc(venueName)}</h2>
      ${tabs}
      <div class="m-units">${unit ? unitCard(group, false) : emptyLevel()}</div>
    </div>
  `;

  // Mekân sekmesi: tıkla → o mekânın kartını + rengini getir
  content().querySelectorAll<HTMLButtonElement>(".m-floor-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      const i = Number(btn.dataset.idx);
      if (i !== sbGroupIdx) { sbGroupIdx = i; render(); }
    });
  });
  // Çok sekmede aktif sekmeyi görünür alana kaydır (modal scroll'unu etkilemeden)
  const floorsEl = content().querySelector<HTMLElement>(".m-floors");
  const activeTab = floorsEl?.querySelector<HTMLElement>(".m-floor-btn.active");
  if (floorsEl && activeTab) {
    floorsEl.scrollLeft = activeTab.offsetLeft - floorsEl.clientWidth / 2 + activeTab.clientWidth / 2;
  }
  // Galeri: küçük resme tıkla → o ünitenin hero'su değişir
  content().querySelectorAll<HTMLElement>(".m-unit").forEach((card) => {
    const heroEl = card.querySelector<HTMLElement>(".m-hero");
    card.querySelectorAll<HTMLElement>(".m-gthumb").forEach((t) => {
      t.addEventListener("click", () => {
        if (heroEl && t.dataset.src) heroEl.style.backgroundImage = `url('${t.dataset.src}')`;
        card.querySelectorAll(".m-gthumb").forEach((x) => x.classList.remove("active"));
        t.classList.add("active");
      });
    });
  });

  // Tab değişince modal BOYU DEĞİŞMESİN: en fazla içeriğe sahip mekâna göre
  // sabit min-height. Tüm kartları gizli ölçüp en uzununu alıyoruz.
  const unitsEl = content().querySelector<HTMLElement>(".m-units");
  if (unitsEl && groups.length > 1) {
    unitsEl.style.minHeight = "";
    const w = unitsEl.clientWidth;
    if (w > 0) {
      const m = document.createElement("div");
      m.style.cssText = `position:absolute;left:-9999px;top:0;visibility:hidden;pointer-events:none;width:${w}px`;
      m.className = "m-units";
      m.innerHTML = groups.map((g) => unitCard(g, false)).join("");
      document.body.appendChild(m);
      let maxH = 0;
      m.querySelectorAll<HTMLElement>(".m-unit").forEach((c) => { maxH = Math.max(maxH, c.getBoundingClientRect().height); });
      m.remove();
      if (maxH > 0) unitsEl.style.minHeight = `${Math.ceil(maxH)}px`;
    }
  }
}

/**
 * Bir mekânın kartı. `group`, aynı kiracının aynı kattaki ünite satırları —
 * normalde tek eleman; birden fazla kategoride listelenen mekânlarda birden
 * fazla. Kartın içeriği (foto, açıklama, iletişim) grubun BİRİNCİL ünitesinden
 * gelir; kategori çipleri ise grubun hepsinden.
 */
function unitCard(group: ApiUnit[], showTitle = true): string {
  const u = group[0];
  const isVacant = u.status === "vacant" || !u.tenantName;
  const hero = u.photos[0];
  const title = u.tenantName ?? "For Lease";
  const catChips = [...new Set(group.map((g) => g.category))]
    .map((cat) => {
      const { fill, ink } = colorFor(cat);
      return `<span class="m-chip" style="background:${esc(fill)};color:${esc(ink)}">${CATEGORY_LABEL[cat]}</span>`;
    })
    .join("");

  return `
    <div class="m-unit${isVacant ? " m-unit-vacant" : ""}">
      <div class="m-hero"${hero ? ` style="background-image:url('${esc(hero)}')"` : ' data-empty="1"'}>
        ${u.logo ? `<img class="m-logo" src="${esc(u.logo)}" alt="" loading="lazy" onerror="this.style.display='none'">` : ""}
        ${hero ? "" : `<span class="m-hero-empty">No photo</span>`}
        <div class="m-hero-tags">
          ${catChips}
          <span class="m-chip" style="background:${STATUS_BG[u.status]};color:${STATUS_FG[u.status]}">${STATUS_LABEL[u.status]}</span>
        </div>
      </div>
      <div class="m-unit-body">
        ${showTitle ? `<h3 class="m-unit-title">${esc(title)}</h3>` : ""}
        ${isVacant
          ? `<p class="m-desc"><em style="color:#94a3b8">This unit is vacant — get in touch for leasing details.</em></p>`
          : `<p class="m-desc">${esc(u.description) || ""}</p>`}
        ${gallery(u.photos)}
        ${sections(u.sections)}
        ${infoGrid([
          u.area ? ["Area", u.area] : null,
          u.hours ? ["Hours", u.hours] : null,
        ])}
        ${featureChips(u.features)}
        ${contactBlock(u.phones, u.emails, u.websites)}
      </div>
    </div>
  `;
}

function emptyLevel(): string {
  return `<p class="m-desc"><em style="color:#94a3b8">No units on this level.</em></p>`;
}

function gallery(photos: string[]): string {
  if (photos.length < 2) return "";
  return `<div class="m-gallery">${photos
    .map((s, i) => `<button class="m-gthumb${i === 0 ? " active" : ""}" data-src="${esc(s)}" style="background-image:url('${esc(s)}')" aria-label="Photo ${i + 1}"></button>`)
    .join("")}</div>`;
}

function sections(secs: { title: string; text: string }[] | null): string {
  if (!secs?.length) return "";
  return `<div class="m-sections">${secs
    .map((s) => `<div class="m-section"><h4>${esc(s.title)}</h4><p>${esc(s.text)}</p></div>`)
    .join("")}</div>`;
}

function infoGrid(rows: ([string, string] | null)[]): string {
  const items = rows.filter(Boolean) as [string, string][];
  if (!items.length) return "";
  // Sütun sayısı = öğe sayısı (max 2) → tek öğede boş kutu kalmaz
  const cols = Math.min(items.length, 2);
  return `<div class="m-info" style="grid-template-columns:repeat(${cols},1fr)">${items
    .map(([k, val]) => `<div class="m-info-row"><span>${esc(k)}</span><b>${esc(val)}</b></div>`)
    .join("")}</div>`;
}

function featureChips(features: string[]): string {
  if (!features?.length) return "";
  return `<div class="m-features">${features.map((f) => `<span>${esc(f)}</span>`).join("")}</div>`;
}

export function contactBlock(phones: string[], emails: string[], websites: string[]): string {
  const rows = [
    ...clean(phones).map(
      (p) => `<a href="tel:${esc(p.replace(/\s/g, ""))}"><span>📞</span>${esc(p)}</a>`,
    ),
    ...clean(emails).map(
      (e) => `<a href="mailto:${esc(e)}"><span>✉️</span>${esc(e)}</a>`,
    ),
    ...clean(websites).map(
      (w) => `<a href="${esc(siteHref(w))}" target="_blank" rel="noopener"><span>🌐</span>${esc(w)}</a>`,
    ),
  ];
  if (!rows.length) return "";
  return `<div class="m-contact">${rows.join("")}</div>`;
}

function clean(values: string[] | null | undefined): string[] {
  return (values ?? []).map((v) => v.trim()).filter(Boolean);
}

// Panelde girilen adres bazen "www.x.com", bazen "https://www.x.com" oluyor.
// Koşulsuz "https://" eklemek ikincisinde "https://https://..." üretiyordu.
function siteHref(site: string): string {
  return /^https?:\/\//i.test(site) ? site : `https://${site}`;
}

export function closeSidebar() {
  const sb = sidebar();
  sb.setAttribute("aria-hidden", "true");
  sb.classList.remove("open");
}

function show() {
  const sb = sidebar();
  sb.setAttribute("aria-hidden", "false");
  sb.classList.add("open");
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
